/**
 * FileIndexTab — automated file/folder listing for a STORAGE_UNIT asset.
 *
 * Distinct from StorageContentsTab: this is machine-captured and exhaustive
 * (every real file/folder from a scan script upload), not manually curated.
 * See CONTEXT.md for the full distinction.
 *
 * Upload replaces the entire index (no history). Search matches file *and*
 * folder names, scoped to this unit only. Below the search box, a
 * collapsible folder tree shows the same data for browsing without a query.
 */

import { useMemo, useRef, useState } from "react";
import {
  Search,
  Upload,
  Folder,
  FolderOpen,
  File as FileIcon,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
} from "lucide-react";
import {
  useFileIndex,
  useUploadFileIndex,
  useUpdateAsset,
} from "../../hooks/useAssets";
import { Button, Select, Spinner, Badge } from "../ui/index.jsx";
import { formatBytes, formatDateTime, formatRelative } from "../../utils/format";
import { DRIVE_HEALTH } from "../../utils/constants";

const HEALTH_BADGE_VARIANT = {
  OK: "success",
  BROKEN: "warning",
  NOT_DETECTED: "warning",
  DEAD: "danger",
};

// ---------------------------------------------------------------------------
// Tree building — flat entries (each carrying a full relative path) into a
// nested structure. Intermediate folders are synthesized from path segments
// even if the scan didn't emit an explicit entry for them, so the tree is
// never broken by an incomplete scan.
// ---------------------------------------------------------------------------
function buildTree(entries) {
  const root = { name: "", path: "", isDirectory: true, children: new Map() };

  for (const e of entries) {
    const parts = e.path.split("/").filter(Boolean);
    let node = root;
    let currentPath = "";
    parts.forEach((part, i) => {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      const isLast = i === parts.length - 1;
      if (!node.children.has(part)) {
        node.children.set(part, {
          name: part,
          path: currentPath,
          isDirectory: true,
          children: new Map(),
          sizeBytes: null,
          modifiedAt: null,
        });
      }
      const child = node.children.get(part);
      if (isLast) {
        child.isDirectory = e.isDirectory;
        child.sizeBytes = e.sizeBytes;
        child.modifiedAt = e.modifiedAt;
        child.id = e.id;
      }
      node = child;
    });
  }
  return root;
}

function sortedChildren(node) {
  return [...node.children.values()].sort((a, b) => {
    if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

// ---------------------------------------------------------------------------
// Tree browser
// ---------------------------------------------------------------------------
function TreeNode({ node, depth }) {
  const [expanded, setExpanded] = useState(false);
  const children = node.isDirectory ? sortedChildren(node) : [];

  return (
    <div>
      <button
        type="button"
        onClick={() => node.isDirectory && setExpanded((v) => !v)}
        className="w-full flex items-center gap-1.5 py-1 px-2 rounded hover:bg-slate-50 text-left"
        style={{ paddingLeft: `${depth * 18 + 8}px` }}
      >
        {node.isDirectory ? (
          <>
            {expanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            )}
            {expanded ? (
              <FolderOpen className="h-4 w-4 text-amber-400 shrink-0" />
            ) : (
              <Folder className="h-4 w-4 text-amber-400 shrink-0" />
            )}
          </>
        ) : (
          <>
            <span className="w-3.5 shrink-0" />
            <FileIcon className="h-4 w-4 text-slate-400 shrink-0" />
          </>
        )}
        <span className="text-sm text-slate-700 truncate">{node.name}</span>
        {!node.isDirectory && node.sizeBytes != null && (
          <span className="text-xs text-slate-400 ml-auto shrink-0 pl-2">
            {formatBytes(node.sizeBytes)}
          </span>
        )}
      </button>
      {node.isDirectory && expanded && (
        <div>
          {children.length === 0 ? (
            <p
              className="text-xs text-slate-300 italic py-1"
              style={{ paddingLeft: `${(depth + 1) * 18 + 30}px` }}
            >
              Empty folder
            </p>
          ) : (
            children.map((child) => (
              <TreeNode key={child.path} node={child} depth={depth + 1} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function TreeBrowser({ entries }) {
  const tree = useMemo(() => buildTree(entries), [entries]);
  const roots = sortedChildren(tree);

  if (roots.length === 0) {
    return (
      <p className="text-sm text-slate-400 italic text-center py-8">
        No files or folders indexed yet.
      </p>
    );
  }

  return (
    <div className="border border-slate-200 rounded-lg py-1 max-h-96 overflow-y-auto">
      {roots.map((node) => (
        <TreeNode key={node.path} node={node} depth={0} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flat search results
// ---------------------------------------------------------------------------
function SearchResults({ entries, query }) {
  const q = query.trim().toLowerCase();
  const matches = entries.filter(
    (e) =>
      e.name.toLowerCase().includes(q) || e.path.toLowerCase().includes(q),
  );

  if (matches.length === 0) {
    return (
      <p className="text-sm text-slate-400 italic text-center py-8">
        No files or folders match "{query}".
      </p>
    );
  }

  return (
    <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-96 overflow-y-auto">
      {matches.map((e) => (
        <div
          key={e.id}
          className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50"
        >
          {e.isDirectory ? (
            <Folder className="h-4 w-4 text-amber-400 shrink-0" />
          ) : (
            <FileIcon className="h-4 w-4 text-slate-400 shrink-0" />
          )}
          <span className="text-sm text-slate-800 truncate">{e.path}</span>
          {!e.isDirectory && e.sizeBytes != null && (
            <span className="text-xs text-slate-400 ml-auto shrink-0 pl-2">
              {formatBytes(e.sizeBytes)}
            </span>
          )}
        </div>
      ))}
      <p className="text-xs text-slate-400 px-3 py-1.5 bg-slate-50">
        {matches.length} match{matches.length === 1 ? "" : "es"}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function FileIndexTab({ assetId, canEdit }) {
  const { data, isLoading } = useFileIndex(assetId);
  const uploadMutation = useUploadFileIndex(assetId);
  const healthMutation = useUpdateAsset(assetId);
  const inputRef = useRef();
  const [search, setSearch] = useState("");
  const [uploadError, setUploadError] = useState(null);

  async function handleFileChosen(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadError(null);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed.entries)) {
        throw new Error('File must contain an "entries" array');
      }
      await uploadMutation.mutateAsync({
        entries: parsed.entries,
        driveCapacityBytes: parsed.driveCapacityBytes ?? null,
        driveFreeBytes: parsed.driveFreeBytes ?? null,
      });
    } catch (err) {
      setUploadError(
        err.response?.data?.error ||
          err.message ||
          "Upload failed — check the file is a valid export from the scan script",
      );
    }
  }

  if (isLoading)
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );

  const entries = data?.entries ?? [];

  return (
    <div>
      {/* Header: drive health + last scanned + upload */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div>
            <p className="text-xs text-slate-400 mb-1">Drive Health</p>
            {canEdit ? (
              <Select
                value={data?.driveHealth || "OK"}
                onChange={(e) =>
                  healthMutation.mutate({ driveHealth: e.target.value })
                }
                options={Object.entries(DRIVE_HEALTH).map(([value, label]) => ({
                  value,
                  label,
                }))}
                className="!py-1 text-xs"
              />
            ) : (
              <Badge variant={HEALTH_BADGE_VARIANT[data?.driveHealth] || "default"}>
                {DRIVE_HEALTH[data?.driveHealth] || "OK"}
              </Badge>
            )}
          </div>
          <div>
            <p className="text-xs text-slate-400 mb-1">Last Scanned</p>
            <p className="text-sm text-slate-700">
              {data?.lastScannedAt ? (
                <span title={formatDateTime(data.lastScannedAt)}>
                  {formatRelative(data.lastScannedAt)}
                </span>
              ) : (
                <span className="text-slate-400 italic">Never scanned</span>
              )}
            </p>
          </div>
        </div>

        {canEdit && (
          <div>
            <input
              ref={inputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={handleFileChosen}
            />
            <Button
              size="sm"
              variant="secondary"
              isLoading={uploadMutation.isPending}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-4 w-4" /> Upload File Index
            </Button>
          </div>
        )}
      </div>

      {(data?.driveHealth && data.driveHealth !== "OK") && (
        <div className="flex items-start gap-2 mb-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            Drive marked <strong>{DRIVE_HEALTH[data.driveHealth]}</strong> —
            the listing below is last-known contents, not necessarily current.
          </span>
        </div>
      )}

      {uploadError && (
        <p className="text-xs text-rose-600 mb-3">{uploadError}</p>
      )}

      {entries.length === 0 ? (
        <p className="text-sm text-slate-400 italic text-center py-10">
          No file index uploaded yet.
          {canEdit && " Run the scan script on this drive, then upload the export above."}
        </p>
      ) : (
        <>
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${entries.length.toLocaleString()} files and folders...`}
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {search.trim() ? (
            <SearchResults entries={entries} query={search} />
          ) : (
            <TreeBrowser entries={entries} />
          )}
        </>
      )}
    </div>
  );
}
